import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { Box, Card, Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { formatDate, formatDuration, formatVideoName, formatUserName } from '../../utils/formatUtils';
import { getChipColor, getChipLabel, getProcessStageLabel } from '../../utils/processStageUtils';
import LanguagePopover from '../common/LanguagePopover';

/**
 * List item component for displaying files in list view
 * 
 * @param {Object} props Component props
 * @param {Object} props.file The file object to display
 * @param {Function} props.onFileClick Handler for when the file is clicked
 * @param {Function} props.onMenuOpen Handler for when the context menu is opened
 * @param {boolean} props.showPath Whether to display the file path (for search results)
 */
const FileListItem = ({ file, onFileClick, onMenuOpen, showPath, className }) => {
    // Extract and format the parent path for display
    const displayPath = React.useMemo(() => {
        if (!showPath || !file.path) return null;
        
        // Format the path for display
        const pathParts = file.path.split('/').filter(Boolean);
        return '/' + pathParts.join('/');
    }, [file.path, showPath]);
    
    // Handle click events
    const handleClick = () => {
        if (onFileClick) {
            onFileClick(file);
        }
    };

    const handleMenuClick = (event) => {
        // Stop propagation to prevent file click
        event.stopPropagation();
        
        if (onMenuOpen) {
            onMenuOpen(event, file);
        }
    };

    // Get updated by information
    const updatedBy = file.updatedBy || 'Unknown';
    
    // Get language information with process stages
    const languagesWithStatus = file.languages || [];

    // Clean up the language state management
    const [languagePopover, setLanguagePopover] = React.useState({
        open: false,
        anchorEl: null
    });

    const handleLanguageCountClick = (event) => {
        // Stop propagation to prevent opening the file
        event.stopPropagation();
        event.preventDefault();
        
        // Set the anchor element for the popover
        setLanguagePopover({
            open: true,
            anchorEl: event.currentTarget
        });
    };
    
    const handleLanguagePopoverClose = () => {
        setLanguagePopover({
            open: false,
            anchorEl: null
        });
    };

    // Add this function inside your FileGridItem component, after the languagesWithStatus declaration
    const getFilteredLanguages = () => {
        try {
            // Get user info from localStorage
            const userInfo = JSON.parse(localStorage.getItem('authUser')) || {};
            const isAdmin = userInfo.role === 'ADMIN' || userInfo.isAdmin;
            const userLanguageAccess = userInfo.languageAccess || {};
            
            // If admin, show all languages
            if (isAdmin) {
            return languagesWithStatus;
            }
            
            // For non-admins, filter languages
            return languagesWithStatus.filter(lang => {
            // Get language name from lang.name
            const langName = lang.name;
            
            if (!langName) return false;
            
            // Always show English and Gujarati (case insensitive)
            const langNameLower = langName.toLowerCase();
            if (langNameLower === 'english' || langNameLower === 'gujarati') {
                return true;
            }
            
            // Show languages where user has write access
            return userLanguageAccess[langName] && 
                    userLanguageAccess[langName].includes('WRITE');
            });
        } catch (error) {
            console.error('Error filtering languages:', error);
            return languagesWithStatus; // Return all languages if there's an error
        }
    };

    // Get filtered languages
    const filteredLanguages = getFilteredLanguages();

    return (
        <Card 
            sx={{ 
                mb: 0, // Remove bottom margin as we're using gap in parent
                p: 1, // Increase padding from 1 to 1.5
                cursor: 'pointer',
                transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                }
            }}
            onClick={handleClick}
            className="file-list-item"
        >
            <Grid 
                container 
                spacing={2} 
                className='file-folder-item'
                alignItems="center" 
                sx={{ 
                    px: 1, 
                    py: 0.25 
                }}
            >
                <Grid item xs={12} sm={4} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} className="file-folder-name">
                        <VideoFileIcon color="secondary" sx={{ mr: 2, fontSize: '2rem' }} />
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography 
                                variant="body1" 
                                noWrap
                                title={file.videoName || file.name}
                            >
                                {formatVideoName(file.videoName || file.name)}
                            </Typography>
                            
                            {file.data && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    Video ID: {file.data}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    
                    {/* Display path in search results */}
                    {showPath && displayPath && (
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            noWrap
                            sx={{ ml: 5, mt: 0.5 }}
                        >
                            <FolderOpenIcon 
                                sx={{ fontSize: '0.8rem', mr: 0.5, verticalAlign: 'text-bottom' }} 
                            />
                            {displayPath}
                        </Typography>
                    )}
                </Grid>
                
                <Grid item xs={6} sm={3} md={2}>
                    <Box 
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} 
                        className="folder-file-details"
                    >
                        {filteredLanguages && filteredLanguages.length > 0 ? (
                            filteredLanguages.slice(0, 3).map(lang => (
                                <Tooltip 
                                    key={lang.name}
                                    title={`${lang.name} (${getProcessStageLabel(lang.processStage)})`}
                                    arrow
                                >
                                    <Chip 
                                        label={getChipLabel(lang.name, lang.processStage)}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent bubbling
                                            e.preventDefault();
                                            handleLanguageCountClick(e); // Call the same handler
                                        }}
                                        sx={{ 
                                            fontSize: '0.7rem',
                                            backgroundColor: `${getChipColor(lang.processStage)}20`,
                                            color: getChipColor(lang.processStage),
                                            borderColor: getChipColor(lang.processStage),
                                            border: '1px solid',
                                            fontWeight: 500,
                                            borderRadius: '16px',
                                            cursor: 'pointer'  // Add pointer cursor
                                        }}
                                    />
                                </Tooltip>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                
                                -
                            </Typography>
                        )}
                        {filteredLanguages.length > 3 && (
                            <Tooltip title={`${filteredLanguages.length - 3} more languages`}>
                                <Chip 
                                    label={`+${filteredLanguages.length - 3}`}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleLanguageCountClick(e);
                                    }}
                                    sx={{ 
                                        fontSize: '0.7rem',
                                        backgroundColor: 'rgba(0,0,0,0.08)',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={1}>
                    <Box className="file-folder-duration" sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimerIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {formatDuration(file.duration)}
                        </Typography>
                    </Box>
                </Grid>
                
                <Grid item xs={6} sm={2} md={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} className="file-folder-updated-by">
                        <PersonIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {formatUserName(updatedBy)}
                        </Typography>
                    </Box>
                </Grid>
                
                <Grid item xs={6} sm={2} md={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} className="file-folder-updated-at">
                        <AccessTimeFilledIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {formatDate(file.updatedAt || file.updatedAt)}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={6} sm={2} md={1} className="list-file-menu">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton
                            className="menu-button list-file-menu" // Added class for help overlay selector
                            size="small"
                            onClick={handleMenuClick}
                            data-testid={`file-menu-${file.videoName || file.name}`}
                            aria-label="more options"
                            className="file-folder-action-menu-button" 
                            sx={{
                                opacity: { xs: 1, sm: 0.5 }, // More visible on mobile
                                visibility: 'visible',
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.08)'
                                }
                            }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Grid>
            </Grid>

                <LanguagePopover 
                    open={languagePopover.open}  // Force open
                    anchorEl={languagePopover.anchorEl}
                    onClose={handleLanguagePopoverClose}
                    languages={filteredLanguages || []}
                    videoLanguages={file.videoLanguages || []}
                />
        </Card>
    );
};

export default FileListItem;